import { Breadcrumb, Button, Layout, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { aggregatorsListAction } from '~Aggregators/actions';
import { aggregatorsSelector } from '~Aggregators/selectors';
import { applicationsOutstandingBrokerCommissionsSelector } from './selectors';
import IAggregator from '~Api/Aggregator/IAggregator';
import IApplication from '~Api/Application/IApplication';
import IDeal from '~Api/Deal/IDeal';
import {
    applicationMarkBrokerCommissionPaidAction,
    applicationsOutstandingBrokerCommissionsListAction,
} from '~Applications/actions';
import { IDictionary } from '~utilities/IDictionary';
import { currencyFormatter } from '~utilities/formatters';
import BrokerCommissionFormatEnum from '~Api/Application/BrokerCommissionFormatEnum';
import InvoiceTypeEnum from '~Api/Aggregator/InvoiceTypeEnum';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import zoneTypeLabels from '~Api/Deal/ZoneTypeLabels';
import codeTypeLabels from '~Api/Application/codeTypeLabels';
import UploadedConditionDocumentList from './Application/UploadedConditionDocumentList';
import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';

export default function OutstandingBrokerCommissions(): ReactElement {
    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const applications: IDictionary<IApplication> = useSelector(applicationsOutstandingBrokerCommissionsSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, [
        aggregators,
        dispatch,
    ]);

    useEffect(() => {
        if (!applications) {
            dispatch(applicationsOutstandingBrokerCommissionsListAction());
        }
    }, [
        applications,
        dispatch,
    ]);

    if (!aggregators || !applications) {
        return (
            <Layout className='brokers'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/applications'>Applications</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Outstanding Broker Commissions</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Outstanding Broker Commissions</Typography.Title>
                        <Spin />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const aggregatorsInResponse: IDictionary<IAggregator> = {};
    let hasBrokerWithoutAggregator: boolean = false;

    _.each(applications, (application: IApplication) => {
        const aggregatorUuid: string = application.deal.broker.aggregatorUuid;

        if (!aggregatorUuid) {
            hasBrokerWithoutAggregator = true;
        } else if (!aggregatorsInResponse[aggregatorUuid]) {
            aggregatorsInResponse[aggregatorUuid] = aggregators[aggregatorUuid];
        }
    });

    const columns: ColumnType<IApplication>[] = [
        {
            dataIndex: 'settlementDate',
            defaultSortOrder: 'ascend',
            fixed: 'left',
            render: (settlementDate: string) => dayjs(settlementDate).format('D/M/YYYY'),
            sorter: (a: IApplication, b: IApplication) => dayjs(a.settlementDate) > dayjs(b.settlementDate) ? 1 : -1,
            title: 'Settled',
            width: 100,
        },
        {
            fixed: 'left',
            render: (application: IApplication) => <Link to={`/applications/${application.uuid}`}>{application.code}</Link>,
            title: 'Code',
            width: 160,
        },
        {
            render: (application: IApplication) => <Link to={`/applications/${application.uuid}`}>{application.formattedName || '-'}</Link>,
            title: 'Application',
            width: 300,
        },
        {
            dataIndex: 'deal',
            render: (deal: IDeal) => deal.brokerUuid ? <Link to={`/brokers/${deal.brokerUuid}`}>{deal.broker.firstName} {deal.broker.lastName}</Link> : '-',
            title: 'Broker',
            width: 300,
        },
        {
            filters: [
                ...(hasBrokerWithoutAggregator ? [{ text: 'None', value: null }] : []),
                ..._.sortBy(aggregatorsInResponse, 'name').map((aggregator: IAggregator) => ({
                    text: aggregator.name,
                    value: aggregator.uuid,
                })),
            ],
            onFilter: (value: boolean | number | string, application: IApplication) => application.deal.broker.aggregatorUuid === value,
            render: (application: IApplication) => {
                if (!application.deal.brokerUuid) {
                    return '-';
                }

                const invoicedDirectlyTag: JSX.Element = application.deal.broker.aggregatorUuid && aggregators[application.deal.broker.aggregatorUuid].invoiceType === InvoiceTypeEnum.DIRECT ? (<Tag title='Invoiced Directly' color='blue'>DIRECT</Tag>) : null;

                return (
                    <Space align='center'>
                        {application.deal.broker.aggregatorUuid ? aggregators[application.deal.broker.aggregatorUuid].name : '-'}
                        {invoicedDirectlyTag}
                    </Space>
                );
            },
            title: 'Aggregator',
            width: 300,
        },
        {
            render: (application: IApplication) => currencyFormatter.format(application.loanAmount),
            title: 'Loan Amount',
            width: 150,
        },
        {
            render: (application: IApplication) => codeTypeLabels[application.codeType] || '-',
            title: 'Loan Type',
            width: 180,
        },
        {
            render: (application: IApplication) => {
                let highestPropertyValue: number = 0;
                let primaryProperty: IApplicationProperty = null;

                _.each(application.properties, (property: IApplicationProperty) => {
                    const propertyValue: number = property.valuationValue || property.dealProperty.estimatedValue;

                    if (propertyValue > highestPropertyValue) {
                        primaryProperty = property;
                        highestPropertyValue = propertyValue;
                    }
                });

                return zoneTypeLabels[primaryProperty.dealProperty.zoneType];
            },
            title: 'Primary Property Type',
            width: 250,
        },
        {
            render: (application: IApplication) => (
                <UploadedConditionDocumentList
                    application={application}
                    conditionType={ConditionTypeEnum.BrokerInvoice}
                />
            ),
            title: 'Invoice',
            width: 100,
        },
        {
            fixed: 'right',
            render: (application: IApplication) => {
                if (application.brokerCommissionFormat === BrokerCommissionFormatEnum.Dollars) {
                    return currencyFormatter.format(application.brokerCommissionDollars);
                }

                return currencyFormatter.format(Math.ceil((application.brokerCommissionPercentage / 100) * application.loanAmount));
            },
            title: 'Commission',
            width: 130,
        },
        {
            dataIndex: 'uuid',
            fixed: 'right',
            render: (uuid: string) => {
                function onClickMarkAsPaid(): void {
                    Modal.confirm({
                        content: 'Are you sure you want to mark this invoice as paid?',
                        okText: 'Mark as Paid',
                        onOk: () => {
                            dispatch(applicationMarkBrokerCommissionPaidAction(uuid));
                        },
                        title: 'Mark as Paid',
                    });
                }

                return <Button type='primary' onClick={onClickMarkAsPaid}>Mark As Paid</Button>;
            },
            title: 'Actions',
            width: 150,
        },
    ];

    return (
        <Layout className='brokers'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/applications'>Applications</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Outstanding Broker Commissions</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Typography.Title level={2}>Outstanding Broker Commissions</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(applications)}
                        pagination={false}
                        rowKey='uuid'
                        scroll={{ x: 1370 }}
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
